import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";

import { useServiceRequests } from "../../../hooks/useServiceRequests";
import EmptyRequests from "../../../assets/images/empty-services-requested.png";

import ServiceContractCard from "./ServiceContractCard";
import { ProgramCard, ProgramCardLoader } from "./ProgramCard";
import { ServiceRequestCard, ServiceRequestCardLoader } from "./ServiceRequestCard";

const RequestsListing = () => {
  const { t } = useTranslation("goService", { keyPrefix: "myRequestsPage" });
  const { requests, contracts, programs, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useServiceRequests();

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const isListLoading = isLoading || isFetchingNextPage;

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "services";
  const category = searchParams.get("category") || "bought-programs";

  if (isListLoading) {
    return (
      <Box sx={styles.container}>
        {type === "services" && Array.from({ length: 2 }).map((_, index) => <ServiceRequestCardLoader key={index} />)}
        {type === "programs" && Array.from({ length: 2 }).map((_, index) => <ProgramCardLoader key={index} />)}
      </Box>
    );
  }

  if (programs && programs.length > 0)
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.title}>
          {category === "bought-programs" ? t("joinedPrograms") : t("latestJoinedPrograms")}
        </Typography>
        {programs.map((program) => (
          <ProgramCard key={program.id} program={program} />
        ))}
        {isListLoading && Array.from({ length: 2 }).map((_, index) => <ProgramCardLoader key={index} />)}
        <Box ref={ref} />
      </Box>
    );

  if (requests && requests.length > 0)
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.title}>{t("negotiatedServices")}</Typography>
        {requests.map((request) => (
          <ServiceRequestCard key={request.id} request={request} />
        ))}
        {isListLoading && Array.from({ length: 2 }).map((_, index) => <ServiceRequestCardLoader key={index} />)}
        <Box ref={ref} />
      </Box>
    );

  if (contracts && contracts.length > 0)
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.title}>{t("negotiatedServices")}</Typography>
        {contracts.map((contract) => (
          <ServiceContractCard key={contract.id} contract={contract} />
        ))}
        {isListLoading && Array.from({ length: 2 }).map((_, index) => <ServiceRequestCardLoader key={index} />)}
        <Box ref={ref} />
      </Box>
    );

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("negotiatedServices")}</Typography>
      <Box sx={styles.emptyContainer}>
        <Box component="img" src={EmptyRequests} alt="empty requests" sx={styles.emptyImage} />
        <Typography sx={styles.emptyTitle}>{t("noServicesRequestedYet")}</Typography>
      </Box>
    </Box>
  );
};

export default RequestsListing;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    height: "fit-content",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  emptyImage: {
    width: "60px",
    height: "92px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  emptyTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
};
